/** @file silhouetteOf: the screen outline of the gizmo's unit sphere in display space (an ellipse). */
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';

/**
 * Display space scales world z by `zScaleView`, so the rings' sphere projects to an ellipse: with `M` = the view's
 * right and up rows times that scale, the outline of `M (unit sphere)` has semi-axes `sqrt(eig(M M^T))`. Writes
 * `[major, minor, angle]` into `out` (unit-sphere radii; `angle` in radians from screen right toward screen up).
 */
export function silhouetteOf(view: View, out: [number, number, number] = [1, 1, 0]): [number, number, number] {
  const { right: r, up: u } = view.basis;
  const z2 = view.zScaleView * view.zScaleView;
  const rr = r[0] * r[0] + r[1] * r[1] + z2 * r[2] * r[2];
  const uu = u[0] * u[0] + u[1] * u[1] + z2 * u[2] * u[2];
  const ru = r[0] * u[0] + r[1] * u[1] + z2 * r[2] * u[2];
  const mid = (rr + uu) / 2;
  const half = Math.hypot((rr - uu) / 2, ru);
  out[0] = Math.sqrt(mid + half);
  out[1] = Math.sqrt(Math.max(0, mid - half));
  out[2] = 0.5 * Math.atan2(2 * ru, rr - uu);
  return out;
}
